import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import apiService from '../services/api';
import { getPolymerFamily } from '../utils/tdsData';
import './TdsPage.css';

const TdsPage = () => {
  const [rawGrades, setRawGrades] = useState([]);
  const [grades, setGrades] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedFamily, setSelectedFamily] = useState('ALL');
  const navigate = useNavigate();

  // SEO Optimization & Analytics setup
  useEffect(() => {
    document.title = "Technical Data Sheets (TDS) - Polymer Catalog | BSB Fortrade";
    
    // Add meta description dynamically
    let metaDesc = document.querySelector('meta[name="description"]');
    if (!metaDesc) {
      metaDesc = document.createElement('meta');
      metaDesc.name = "description";
      document.head.appendChild(metaDesc);
    }
    metaDesc.content = "Download and view high-quality Technical Data Sheets (TDS) for prime polymer grades including PVC, PP, HDPE, LDPE, and LLDPE from major manufacturers like LG, Formosa, and Tianjin Dagu.";
  }, []);

  // Fetch grades from existing live rate API
  useEffect(() => {
    const fetchGrades = async () => {
      try {
        setLoading(true);
        const response = await apiService.getPolymerLiveRate();
        
        let dataArray = [];
        if (response && response.success && Array.isArray(response.data)) {
          dataArray = response.data;
        } else if (response && Array.isArray(response.data)) {
          dataArray = response.data;
        }

        if (dataArray.length > 0) {
          // Process and deduplicate grades by code_name
          const seenCodes = new Set();
          const processedList = [];

          dataArray.forEach((item) => {
            const code = (item.code_name || item.code || '').trim();
            if (!code || seenCodes.has(code.toUpperCase())) return;

            seenCodes.add(code.toUpperCase());

            const family = getPolymerFamily(item.grade_name);

            processedList.push({
              id: item.id || code,
              codeName: code,
              gradeName: item.grade_name || 'Polymer',
              brandName: item.brand_name || 'Generic',
              processName: item.process_name || 'Moulding',
              mfiValue: item.mfi_value || item.mfi || 'N/A',
              polymerFamily: family
            });
          });

          // Sort grades alphabetically by codeName
          processedList.sort((a, b) => a.codeName.localeCompare(b.codeName));

          setRawGrades(processedList);
          setGrades(processedList);
        }
      } catch (err) {
        console.error('Error fetching grades for TDS:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchGrades();
  }, []);

  // Apply real-time search & filters
  useEffect(() => {
    let filtered = [...rawGrades];

    // Filter by Polymer Family pill
    if (selectedFamily !== 'ALL') {
      filtered = filtered.filter(item => item.polymerFamily === selectedFamily);
    }

    // Filter by search query
    if (searchQuery.trim() !== '') {
      const q = searchQuery.toLowerCase().trim();
      filtered = filtered.filter(item => 
        item.codeName.toLowerCase().includes(q) ||
        item.gradeName.toLowerCase().includes(q) ||
        item.brandName.toLowerCase().includes(q) ||
        item.processName.toLowerCase().includes(q) ||
        item.polymerFamily.toLowerCase().includes(q)
      );
    }

    setGrades(filtered);
  }, [searchQuery, selectedFamily, rawGrades]);

  // Statistics calculation
  const totalGradesCount = rawGrades.length;
  const uniqueBrands = new Set(rawGrades.map(g => g.brandName.toUpperCase())).size;
  const families = ['PVC', 'PP', 'HDPE', 'LDPE', 'LLDPE'];

  const getFamilyBadgeClass = (family) => {
    switch (family) {
      case 'PVC': return 'badge-pvc';
      case 'PP': return 'badge-pp';
      case 'HDPE': return 'badge-hdpe';
      case 'LDPE': return 'badge-ldpe';
      case 'LLDPE': return 'badge-lldpe';
      default: return 'badge-default';
    }
  };

  const handleDownloadPDFDirect = (e, code) => {
    e.stopPropagation();
    e.preventDefault();
    // Navigate to detail page first and trigger a auto-print command
    navigate(`/tds/${code}?download=true`);
  };

  return (
    <>
      {/* Search Header Banner */}
      <section className="page-header">
        <div className="container">
          <h1>Technical Data Sheets</h1>
          <p>Search, view, and download TDS sheets for premium polymer grades.</p>
        </div>
      </section>

      <div className="tds-container">
        {/* Quick Stats Grid */}
        <div className="tds-stats-bar">
          <div className="tds-stat-card">
            <div className="tds-stat-icon">
              <i className="fas fa-file-invoice"></i>
            </div>
            <div className="tds-stat-info">
              <h4>Total TDS Available</h4>
              <p>{loading ? '...' : totalGradesCount}</p>
            </div>
          </div>
          <div className="tds-stat-card">
            <div className="tds-stat-icon">
              <i className="fas fa-copyright"></i>
            </div>
            <div className="tds-stat-info">
              <h4>Supported Brands</h4>
              <p>{loading ? '...' : uniqueBrands}</p>
            </div>
          </div>
          <div className="tds-stat-card">
            <div className="tds-stat-icon">
              <i className="fas fa-cubes"></i>
            </div>
            <div className="tds-stat-info">
              <h4>Polymer Families</h4>
              <p>{families.length}</p>
            </div>
          </div>
        </div>

        {/* Filter and Search Panel */}
        <div className="tds-search-box">
          <div className="tds-search-grid">
            <div className="tds-input-wrapper">
              <i className="fas fa-search"></i>
              <input
                type="text"
                placeholder="Search by Grade Code (e.g. TL700, DG700), Brand, or Grade..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="tds-search-input"
              />
            </div>
            <div className="tds-filter-group">
              <button
                className={`tds-filter-btn ${selectedFamily === 'ALL' ? 'active' : ''}`}
                onClick={() => setSelectedFamily('ALL')}
              >
                All Families
              </button>
              {families.map(family => (
                <button
                  key={family}
                  className={`tds-filter-btn ${selectedFamily === family ? 'active' : ''}`}
                  onClick={() => setSelectedFamily(family)}
                >
                  {family}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Main List Table */}
        {loading ? (
          <div className="tds-table-container">
            <div className="tds-loading">
              <i className="fas fa-spinner fa-spin"></i>
              <h3>Loading Polymer Datasheets...</h3>
              <p>Fetching the latest grade specifications from BSB network.</p>
            </div>
          </div>
        ) : grades.length > 0 ? (
          <div className="tds-cards-grid">
            {grades.map((grade) => (
              <div key={grade.codeName} className="tds-card">
                <div className="tds-card-header">
                  <h3 className="tds-card-title">{grade.codeName}</h3>
                  <span className={`tds-badge ${getFamilyBadgeClass(grade.polymerFamily)}`}>
                    {grade.gradeName}
                  </span>
                </div>
                <div className="tds-card-body">
                  <div className="tds-card-detail">
                    <span className="tds-card-detail-label">Brand</span>
                    <span className="tds-card-detail-value">{grade.brandName}</span>
                  </div>
                  <div className="tds-card-detail">
                    <span className="tds-card-detail-label">Process</span>
                    <span className="tds-card-detail-value">{grade.processName}</span>
                  </div>
                  <div className="tds-card-detail">
                    <span className="tds-card-detail-label">MFI / K-Value</span>
                    <span className="tds-card-detail-value text-secondary" style={{ fontStyle: 'italic' }}>
                      {grade.mfiValue}
                    </span>
                  </div>
                  <div className="tds-card-detail">
                    <span className="tds-card-detail-label">TDS Available</span>
                    <span className="tds-card-detail-value" style={{ color: '#166534' }}>
                      <i className="fas fa-circle-check" style={{ marginRight: '6px' }}></i> Yes
                    </span>
                  </div>
                </div>
                <div className="tds-card-footer">
                  <Link to={`/tds/${grade.codeName}`} className="tds-btn-view">
                    <i className="far fa-eye"></i> View PDF
                  </Link>
                  <button
                    onClick={(e) => handleDownloadPDFDirect(e, grade.codeName)}
                    className="tds-btn-download"
                  >
                    <i className="fas fa-download"></i> Download
                  </button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="tds-table-container">
            <div className="tds-empty-state">
              <i className="fas fa-file-circle-xmark"></i>
              <h3>No Technical Datasheets Found</h3>
              <p>We couldn't find any matches for "{searchQuery}" under "{selectedFamily}". Please try checking your spelling or select another category.</p>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default TdsPage;
